import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@/components/ui/button';
import { FullScreenLoader } from '@/components/ui/full-screen-loader';
import { isStaleChunkError } from '@/lib/staleChunkError';
import * as Sentry from '@sentry/react';
import i18next from 'i18next';
import { AlertTriangle } from 'lucide-react';
import { Component } from 'react';
const STALE_CHUNK_RELOAD_KEY = 'nl.staleChunkReloadedAt';
// A second chunk failure inside this window means the reload didn't fix it
// (the failure isn't staleness), so fall through to the manual error screen
// instead of reloading again.
const STALE_CHUNK_RELOAD_WINDOW_MS = 60_000;
export class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, reloadingStaleChunk: false };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }
    componentDidCatch(error, errorInfo) {
        // A lazy route chunk failed to load. In production this is almost always
        // a tab whose index.html predates the current deploy asking for a hash
        // that no longer exists (nginx 404s it) — one reload fetches the current
        // bundle. The sessionStorage timestamp guards against a reload loop when
        // the failure has some other cause (e.g. network down).
        if (isStaleChunkError(error) && this.markStaleChunkReload()) {
            Sentry.captureException(error, {
                tags: { staleChunkReload: true },
                contexts: { react: { componentStack: errorInfo.componentStack } },
            });
            this.setState({ reloadingStaleChunk: true });
            window.location.reload();
            return;
        }
        console.error('Unhandled render error:', error, errorInfo);
        Sentry.captureException(error, {
            contexts: { react: { componentStack: errorInfo.componentStack } },
        });
    }
    // Returns true (and stamps the attempt) unless a reload was already tried
    // within the loop window. sessionStorage can throw (blocked storage) — treat
    // that as "don't auto-reload" so we can never loop unguarded.
    markStaleChunkReload() {
        try {
            const last = Number(sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY));
            if (Number.isFinite(last) && Date.now() - last < STALE_CHUNK_RELOAD_WINDOW_MS) {
                return false;
            }
            sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, String(Date.now()));
            return true;
        }
        catch {
            return false;
        }
    }
    render() {
        if (this.state.reloadingStaleChunk) {
            // Brief moment between location.reload() and the page actually
            // unloading — show a loader, not the error screen.
            return _jsx(FullScreenLoader, {});
        }
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }
            return (_jsx("div", { className: "bg-background flex min-h-dvh items-center justify-center p-4", children: _jsxs("div", { className: "max-w-md text-center", children: [_jsx(AlertTriangle, { className: "text-destructive mx-auto h-12 w-12" }), _jsx("h1", { className: "text-foreground mt-4 text-xl font-semibold", children: i18next.t('error.somethingWentWrong') }), _jsx("p", { className: "text-muted-foreground mt-2 text-sm", children: i18next.t('error.reloadMessage') }), process.env.NODE_ENV === 'development' && this.state.error && (_jsx("pre", { className: "bg-muted text-muted-foreground mt-4 max-h-32 overflow-auto rounded-md p-3 text-left text-xs", children: this.state.error.message })), _jsx(Button, { className: "mt-6", onClick: () => window.location.reload(), children: i18next.t('error.reloadPage') })] }) }));
        }
        return this.props.children;
    }
}
